import { useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { getQueueFileByMonthAndYear, restartQueueFile } from '../api/requests/queue-files';
import { generateMonthlyReports, getMonthlyFilesCount } from '../api/requests/generate-reports';
import { QueueFileStatus } from '../shared/constants';

/**
 * Custom hook for managing queue file polling and PDF generation
 * @param {Object} currentMonth - Object with month and year properties
 * @returns {Object} Object containing queueFilesJob, isGeneratePdfLoading, availableSalariesCount, and handleGenerateMonthlyPdfs function
 */
export default function useQueueFilePolling(currentMonth) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const pollingIntervalRef = useRef(null);
  const [queueFilesJob, setQueueFilesJob] = useState(null);
  const [isGeneratePdfLoading, setIsGeneratePdfLoading] = useState(false);
  const [availableSalariesCount, setAvailableSalariesCount] = useState(0);

  // Check initial queue file status and available salaries count when month changes
  useEffect(() => {
    const checkQueueFileStatus = async () => {
      try {
        const job = await getQueueFileByMonthAndYear(currentMonth.month, currentMonth.year);
        setQueueFilesJob(job || null);
      } catch (error) {
        setQueueFilesJob(null);
      }
    };

    const checkAvailableSalaries = async () => {
      try {
        const count = await getMonthlyFilesCount({ 
          month: currentMonth.month, 
          year: currentMonth.year 
        });
        setAvailableSalariesCount(count);
      } catch (error) {
        setAvailableSalariesCount(0);
      }
    };

    checkQueueFileStatus();
    checkAvailableSalaries();
  }, [currentMonth]);

  // Cleanup polling interval on unmount or month change
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsGeneratePdfLoading(false);
      }
    };
  }, [currentMonth]);

  const startPolling = () => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Start polling every 5 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const job = await getQueueFileByMonthAndYear(currentMonth.month, currentMonth.year);
        setQueueFilesJob(job || null);
        const status = job?.status || null;

        // Stop polling if status is 'done' or 'failed'
        if (status === QueueFileStatus.done || status === QueueFileStatus.failed) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsGeneratePdfLoading(false);
          
          // Always check available salaries count after job completion (done or failed)
          // Job might have processed partially and then failed, so we need to know what's left
          try {
            const count = await getMonthlyFilesCount({ 
              month: currentMonth.month, 
              year: currentMonth.year 
            });
            setAvailableSalariesCount(count);
          } catch (error) {
            setAvailableSalariesCount(0);
          }
          
          if (status === QueueFileStatus.done) {
            enqueueSnackbar(t('pdf-generation-completed'), { variant: 'success' });
          } else {
            enqueueSnackbar(t('pdf-generation-failed'), { variant: 'error' });
          }
        }
      } catch (error) {
        // On error, stop polling and show error
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setIsGeneratePdfLoading(false);
        enqueueSnackbar(t('something-went-wrong'), { variant: 'error' });
      }
    }, 5000);
  };

  const handleGenerateMonthlyPdfs = async () => {
    if (queueFilesJob?.status === QueueFileStatus.failed) {
      // Restart failed job
      try {
        setIsGeneratePdfLoading(true);
        if (queueFilesJob?.id) {
          await restartQueueFile(queueFilesJob.id);
          enqueueSnackbar(t('job-restarted'), { variant: 'success' });
          // Update job status to new after restart
          setQueueFilesJob({ ...queueFilesJob, status: QueueFileStatus.new });
          startPolling();
        }
      } catch (error) {
        enqueueSnackbar(t('something-went-wrong'), { variant: 'error' });
        setIsGeneratePdfLoading(false);
      }
    } else {
      // Create new job
      try {
        setIsGeneratePdfLoading(true);
        await generateMonthlyReports({
          month: currentMonth.month,
          year: currentMonth.year,
        });
        enqueueSnackbar(t('pdf-generation-started'), { variant: 'success' });

        const job = await getQueueFileByMonthAndYear(currentMonth.month, currentMonth.year);
        setQueueFilesJob(job || null);

        startPolling();
      } catch (error) {
        enqueueSnackbar(t('something-went-wrong'), { variant: 'error' });
        setIsGeneratePdfLoading(false);
      }
    }
  };

  return {
    queueFilesJob,
    isGeneratePdfLoading,
    availableSalariesCount,
    handleGenerateMonthlyPdfs,
  };
}

